// Navigation Component
import { signOut, getCurrentUser } from '../firebase/auth.js';
import router from '../router.js';
import { t, getLanguage, setLanguage } from '../i18n.js';
import { showCreditsModal, initCreditsModal } from './CreditsModal.js';

/**
 * Create navigation component
 * @param {Object} user - Current user object
 * @returns {string} HTML string
 */
export function createNavigation(user) {
  const path = router.getPath();
  const params = router.getParams();
  const currentLang = getLanguage();
  const langToggleText = currentLang === 'tr' ? 'EN' : 'TR';
  const nextLang = currentLang === 'tr' ? 'en' : 'tr';

  // Dynamic dashboard link - points to site dashboard if in site context
  const dashboardLink = params.id ? `#/site/${params.id}` : '#/dashboard';

  return `
    <nav class="navbar">
      <div style="display: flex; align-items: center; gap: 1rem;">
        <a href="${dashboardLink}" class="navbar-brand">
          <span class="navbar-brand-icon">🏛️</span>
          <span class="hide-mobile">${t('app.title')}</span>
        </a>
        <button class="btn btn-ghost btn-sm btn-lang-toggle" data-next-lang="${nextLang}">
          🌐 ${langToggleText}
        </button>
      </div>
      
      ${user ? `
        <ul class="navbar-nav hide-mobile">
            <li>
              <a href="${dashboardLink}" class="navbar-link ${path === '/dashboard' || path === `/site/${params.id}` ? 'active' : ''}">
                <span>📊</span>
                <span>${t('nav.dashboard')}</span>
              </a>
            </li>
            ${params.id ? `
            <li>
              <a href="#/site/${params.id}" class="navbar-link ${path === `/site/${params.id}` ? 'active' : ''}">
                <span>📍</span>
                <span>${t('site.tabDetails')}</span>
              </a>
            </li>
            <li>
              <a href="#/site/${params.id}/logbook" class="navbar-link ${path.includes('logbook') ? 'active' : ''}">
                <span>📓</span>
                <span>${t('site.tabLogbook')}</span>
              </a>
            </li>
            <li>
              <a href="#/site/${params.id}/assets" class="navbar-link ${path.includes('assets') ? 'active' : ''}">
                <span>🗿</span>
                <span>${t('site.tabAssets')}</span>
              </a>
            </li>
            <li>
              <a href="#/site/${params.id}/reconstruct" class="navbar-link ${path.includes('reconstruct') ? 'active' : ''}">
                <span>🧩</span>
                <span>${t('site.tabReconstruct')}</span>
              </a>
            </li>
          ` : ''}
        </ul>
        
        <div class="navbar-actions" style="display: flex; align-items: center; gap: 0.5rem;">
            <button class="btn btn-ghost btn-sm nav-credits-btn" id="nav-credits-btn" title="${t('credits.buy')}">
                <span id="nav-credits-badge">💳 ...</span>
            </button>

            <div class="navbar-user">
              <div class="navbar-avatar" title="${user.email}">
                  ${getInitials(user.displayName || user.email)}
              </div>
              <button class="btn btn-ghost btn-sm hide-mobile" id="logout-btn">
                  ${t('nav.logout')}
              </button>
              <button class="btn btn-ghost btn-icon btn-sm show-mobile" id="logout-btn-mobile" title="${t('nav.logout')}">
                🚪
              </button>
            </div>
        </div>
      ` : `
        <div class="navbar-actions" style="display: flex; align-items: center; gap: 1rem;">
            <a href="#/login" class="btn btn-primary">${t('login.button')}</a>
        </div>
      `}
    </nav>
    
    ${user ? `
      <!-- Mobile Bottom Navigation -->
      <div class="navbar-bottom show-mobile-flex">
        <a href="${dashboardLink}" class="nav-item-mobile ${path === '/dashboard' || path === `/site/${params.id}` ? 'active' : ''}">
          <span>📊</span>
          <span>${t('nav.dashboard')}</span>
        </a>
        ${params.id ? `
          <a href="#/site/${params.id}/logbook" class="nav-item-mobile ${path.includes('logbook') ? 'active' : ''}">
            <span>📓</span>
            <span>${t('site.tabLogbook')}</span>
          </a>
          <a href="#/site/${params.id}/assets" class="nav-item-mobile ${path.includes('assets') ? 'active' : ''}">
            <span>🗿</span>
            <span>${t('site.tabAssets')}</span>
          </a>
          <a href="#/site/${params.id}/reconstruct" class="nav-item-mobile ${path.includes('reconstruct') ? 'active' : ''}">
            <span>🧩</span>
            <span>${t('site.tabReconstruct')}</span>
          </a>
        ` : `
          <div class="nav-item-mobile" style="opacity: 0.3">
            <span>📓</span>
            <span>${t('site.tabLogbook')}</span>
          </div>
          <div class="nav-item-mobile" style="opacity: 0.3">
            <span>🗿</span>
            <span>${t('site.tabAssets')}</span>
          </div>
          <div class="nav-item-mobile" style="opacity: 0.3">
            <span>🧩</span>
            <span>${t('site.tabReconstruct')}</span>
          </div>
        `}
      </div>
    ` : ''}
  `;
}

/**
 * Initialize navigation event listeners
 */
export function initNavigation() {
  const logoutBtn = document.getElementById('logout-btn');
  const logoutBtnMobile = document.getElementById('logout-btn-mobile');
  
  const handleLogout = async () => {
    try {
      await signOut();
      router.navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  if (logoutBtn) logoutBtn.addEventListener('click', handleLogout);
  if (logoutBtnMobile) logoutBtnMobile.addEventListener('click', handleLogout);

  const langBtn = document.querySelector('.btn-lang-toggle');
  if (langBtn) {
    langBtn.addEventListener('click', (e) => {
      const nextLang = e.currentTarget.dataset.nextLang;
      setLanguage(nextLang);
    });
  }

  // Credits badge — load balance and wire up modal
  const creditsBtn = document.getElementById('nav-credits-btn');
  if (creditsBtn) {
    initCreditsModal();
    creditsBtn.addEventListener('click', () => showCreditsModal());

    // Load balance asynchronously
    const user = getCurrentUser();
    if (user) {
      import('../firebase/credits.js').then(({ getUserCredits }) => {
        getUserCredits(user.uid).then(credits => {
          const badge = document.getElementById('nav-credits-badge');
          if (badge) badge.textContent = `💳 ${credits}`;
        }).catch(() => {});
      });
    }
  }
}

/**
 * Get initials from name or email
 * @param {string} str 
 * @returns {string}
 */
function getInitials(str) {
  if (!str) return '?';

  const parts = str.split(/[@\s]+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return str.slice(0, 2).toUpperCase();
}
